/* THE FEEDBACK BUTTON ON EVERY SCREEN (Jay, System Project task #1138, 02/11: "User feedback reaches Max only by mail and chat,
   one by one. Nothing is collected in one place"). Bug / Hard to use / Idea and an optional note; the screen goes with it and the
   server adds the role. "My feedback" shows the sender what became of each item. Sending never gets in the way: a failure is kept
   in this browser and sent again the next time the window opens, and the person is thanked either way.
   Which types (and whether a note) come from the config table through /api/loop/config.
   FILES (Max 03/11: "upload file, ảnh hoặc ctrl+V để paste ảnh vào"): up to five, 10 MB each, picked or pasted — a screenshot
   from the clipboard arrives as screenshot-<time>.png. They go one by one right after the feedback is saved; a feedback that
   had to wait in the queue goes without them, and the person is told. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageSquarePlus, Bug, MousePointerClick, Lightbulb, X, Paperclip, FileText } from 'lucide-react';
import { BACKEND_URL } from './backendConfig';
import './Feedback.css';
import { dateLocale } from './l10nField';

const API = `${BACKEND_URL}/api/loop`;
const QUEUE = 'loopFeedbackQueue';
const KINDS = [
  { k: 'bug', Icon: Bug, label: 'xLoop.fbBug', hint: 'xLoop.fbBugHint' },
  { k: 'hard_to_use', Icon: MousePointerClick, label: 'xLoop.fbHard', hint: 'xLoop.fbHardHint' },
  { k: 'idea', Icon: Lightbulb, label: 'xLoop.fbIdea', hint: 'xLoop.fbIdeaHint' },
];
const STATUS = { new: 'xLoop.stNew', seen: 'xLoop.stSeen', planned: 'xLoop.stPlanned', done: 'xLoop.stDone', wont: 'xLoop.stWont' };

const readQueue = () => { try { return JSON.parse(localStorage.getItem(QUEUE) || '[]'); } catch (e) { return []; } };
const writeQueue = (q) => { try { localStorage.setItem(QUEUE, JSON.stringify(q.slice(-10))); } catch (e) { /* private window */ } };
const post = (item) => fetch(`${API}/feedback`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(item) })
  .then(async (r) => { if (!r.ok && r.status !== 400) throw new Error(String(r.status)); return r.ok ? (await r.json().catch(() => ({}))).id || null : null; });
const FILE_MAX_MB = 10;
const FILES_MAX = 5;
const fileUrl = (id) => `${API}/feedback/files/${id}`;
const stamp = () => { const d = new Date(); const p = (n) => String(n).padStart(2, '0'); return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`; };

export default function FeedbackButton() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('send');
  const [conf, setConf] = useState({ kinds: KINDS.map((x) => x.k), note: true });
  const [kind, setKind] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [mine, setMine] = useState(null);
  const [files, setFiles] = useState([]);   // { key, file, url } — url only for an image, to show it small
  const fileInput = useRef(null);
  const [preview, setPreview] = useState(null);   // the picked image, large, over the window
  const screen = location.pathname;
  const addFiles = useCallback((list) => {
    const picked = [...(list || [])];
    if (!picked.length) return;
    const big = picked.filter((f) => f.size > FILE_MAX_MB * 1048576);
    setFiles((cur) => {
      const room = Math.max(0, FILES_MAX - cur.length);
      const ok = picked.filter((f) => f.size <= FILE_MAX_MB * 1048576);
      // every file gets a local address so it can be opened before sending (Max 03/11: "mở hình lên để check lại"); images also show small
      const made = ok.slice(0, room).map((f) => ({ key: `${f.name}-${f.size}-${Math.random()}`, file: f, url: URL.createObjectURL(f), image: /^image\//.test(f.type) }));
      const problems = [...big.map((f) => t('xLoop.fbTooBig', { name: f.name, mb: FILE_MAX_MB })), ...(ok.length > room ? [t('xLoop.fbTooMany', { n: FILES_MAX })] : [])];
      setMsg(problems.length ? { bad: true, text: problems.join(' ') } : null);
      return [...cur, ...made];
    });
  }, [t]);
  // Ctrl+V anywhere in the window: a screenshot on the clipboard becomes a file of this feedback
  const onPaste = (e) => {
    if (tab !== 'send') return;
    const got = [...(e.clipboardData?.items || [])].filter((it) => it.kind === 'file').map((it) => it.getAsFile()).filter(Boolean)
      .map((f) => (f.name && f.name !== 'image.png' ? f : new File([f], `screenshot-${stamp()}.${(f.type.split('/')[1] || 'png').replace('jpeg', 'jpg')}`, { type: f.type || 'image/png' })));
    if (!got.length) return;
    e.preventDefault(); addFiles(got);
  };
  const dropFile = (key) => setFiles((cur) => { const x = cur.find((f) => f.key === key); if (x?.url) URL.revokeObjectURL(x.url); return cur.filter((f) => f.key !== key); });

  const loadMine = useCallback(() => fetch(`${API}/feedback/mine`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : { items: [] }))
    .then((d) => setMine(d.items || [])).catch(() => setMine([])), []);
  // what failed last time goes now, quietly
  const flushQueue = useCallback(async () => {
    const q = readQueue(); if (!q.length) return;
    const left = [];
    for (const item of q) { try { await post(item); } catch (e) { left.push(item); } } // eslint-disable-line no-await-in-loop
    writeQueue(left);
  }, []);
  /* ?fb=mine — the bell line for an answer to one's feedback (Max 03/11) opens My feedback here, and the address loses the mark */
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (q.get('fb') !== 'mine') return;
    setOpen(true); setTab('mine'); setMsg(null);
    q.delete('fb');
    navigate({ pathname: location.pathname, search: q.toString() ? `?${q}` : '' }, { replace: true });
  }, [location.search, location.pathname, navigate]);
  useEffect(() => {
    if (!open) return;
    fetch(`${API}/config`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).then((d) => { if (d) setConf(d); }).catch(() => {});
    flushQueue().then(loadMine);
  }, [open, flushQueue, loadMine]);
  useEffect(() => {
    if (!open) return undefined;
    // Escape closes the large image first, then the window
    const esc = (e) => { if (e.key !== 'Escape') return; if (preview) setPreview(null); else setOpen(false); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [open, preview]);

  const send = async () => {
    if (!kind) { setMsg({ bad: true, text: t('xLoop.fbPick') }); return; }
    const item = { kind, note: conf.note ? note.trim() : '', screen };
    setBusy(true); setMsg(null);
    let id = null; let queued = false;
    try { id = await post(item); } catch (e) { writeQueue([...readQueue(), item]); queued = true; }   // never the person's problem
    const failed = [];
    if (id) {
      for (const f of files) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const r = await fetch(`${API}/feedback/${id}/files?name=${encodeURIComponent(f.file.name)}&type=${encodeURIComponent(f.file.type || 'application/octet-stream')}`,
            { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/octet-stream' }, body: f.file });
          if (!r.ok) failed.push(f.file.name);
        } catch (e) { failed.push(f.file.name); }
      }
    }
    files.forEach((f) => f.url && URL.revokeObjectURL(f.url));
    const hadFiles = files.length > 0;
    setBusy(false); setKind(''); setNote(''); setFiles([]);
    setMsg(queued && hadFiles ? { bad: true, text: t('xLoop.fbQueuedNoFiles') }
      : failed.length ? { bad: true, text: t('xLoop.fbFileFailed', { names: failed.join(', ') }) } : { bad: false, text: t('xLoop.fbThanks') });
    loadMine(); setTab('mine');
  };
  const when = (d) => (d ? new Date(d).toLocaleDateString(dateLocale(i18n.language), { day: '2-digit', month: 'short', year: 'numeric' }) : '');

  return (
    <>
      <button type="button" className="fb-hdr-btn" title={t('xLoop.fbTitle')} aria-label={t('xLoop.fbTitle')} aria-haspopup="dialog"
        aria-expanded={open} onClick={() => { setOpen(true); setTab('send'); setMsg(null); }} data-fb-open>
        <MessageSquarePlus size={16} />
      </button>
      {open && ReactDOM.createPortal(
        <div className="fb-back" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="fb-dialog" role="dialog" aria-modal="true" aria-labelledby="fb-title" onPaste={onPaste} data-fb-dialog>
            <div className="fb-head">
              <h3 id="fb-title">{t('xLoop.fbTitle')}</h3>
              <button type="button" className="fb-x" onClick={() => setOpen(false)} aria-label={t('xLoop.close')}><X size={18} /></button>
            </div>
            <div className="fb-tabs" role="tablist">
              <button type="button" role="tab" aria-selected={tab === 'send'} className={tab === 'send' ? 'on' : ''} onClick={() => setTab('send')} data-fb-tab="send">{t('xLoop.fbTitle')}</button>
              <button type="button" role="tab" aria-selected={tab === 'mine'} className={tab === 'mine' ? 'on' : ''} onClick={() => setTab('mine')} data-fb-tab="mine">
                {t('xLoop.fbMine')}{mine && mine.length ? <em>{mine.length}</em> : null}
              </button>
            </div>
            {msg && <p className={msg.bad ? 'fb-msg bad' : 'fb-msg'} role="status">{msg.text}</p>}
            {tab === 'send' ? (
              <div className="fb-body">
                <p className="fb-intro">{t('xLoop.fbIntro')}</p>
                <div className="fb-kinds" role="radiogroup">
                  {KINDS.filter((x) => conf.kinds.includes(x.k)).map(({ k, Icon, label, hint }) => (
                    <button key={k} type="button" role="radio" aria-checked={kind === k} className={`fb-kind${kind === k ? ' on' : ''}`}
                      onClick={() => { setKind(k); setMsg(null); }} data-fb-kind={k}>
                      <Icon size={20} /><b>{t(label)}</b><span>{t(hint)}</span>
                    </button>
                  ))}
                </div>
                {conf.note && (
                  <label className="fb-note"><span>{t('xLoop.fbNote')}</span>
                    <textarea rows={3} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('xLoop.fbNotePh')} data-fb-note />
                  </label>
                )}
                <div className="fb-files" data-fb-files>
                  {files.length > 0 && (
                    <ul>
                      {files.map((f) => (
                        <li key={f.key} data-fb-file={f.file.name}>
                          {f.image ? (
                            <button type="button" className="fb-thumb" onClick={() => setPreview(f)} title={t('xLoop.fbView')} data-fb-view={f.file.name}><img src={f.url} alt={f.file.name} /></button>
                          ) : <FileText size={18} aria-hidden="true" />}
                          {f.image
                            ? <button type="button" className="fb-fname" onClick={() => setPreview(f)} title={t('xLoop.fbView')}>{f.file.name}</button>
                            : <a className="fb-fname" href={f.url} target="_blank" rel="noopener noreferrer" title={t('xLoop.fbView')}>{f.file.name}</a>}
                          <button type="button" className="fb-x" onClick={() => dropFile(f.key)} aria-label={t('xLoop.fbRemove', { name: f.file.name })}><X size={14} /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {files.length < FILES_MAX && (
                    <div className="fb-attach">
                      <button type="button" className="fb-btn" onClick={() => fileInput.current && fileInput.current.click()} data-fb-attach><Paperclip size={14} /> {t('xLoop.fbAttach')}</button>
                      <span>{t('xLoop.fbPasteHint')}</span>
                    </div>
                  )}
                  <input ref={fileInput} type="file" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-fb-fileinput />
                </div>
                <p className="fb-screen">{t('xLoop.fbScreen')}: <code>{screen}</code></p>
                <div className="fb-foot">
                  <button type="button" className="fb-btn" onClick={() => setOpen(false)}>{t('xLoop.close')}</button>
                  <button type="button" className="fb-btn primary" disabled={busy} onClick={send} data-fb-send>{busy ? t('xLoop.fbSending') : t('xLoop.fbSend')}</button>
                </div>
              </div>
            ) : (
              <div className="fb-body fb-mine" data-fb-mine>
                {mine === null ? <p className="fb-intro">…</p> : !mine.length ? <p className="fb-intro">{t('xLoop.fbNone')}</p> : mine.map((x) => {
                  const K = KINDS.find((y) => y.k === x.kind);
                  return (
                    <div key={x.id} className="fb-item" data-fb-item={x.id}>
                      <div className="fb-item-top">
                        {x.code && <code className="fb-code" data-fb-code={x.id}>{x.code}</code>}
                        <b>{K ? t(K.label) : x.kind}</b>
                        <span className={`fb-st st-${x.status}`}>{t(STATUS[x.status] || 'xLoop.stNew')}</span>
                        <span className="fb-when">{when(x.createdAt)} · <code>{x.screen}</code></span>
                      </div>
                      {x.note && <p>{x.note}</p>}
                      {(x.files || []).length > 0 && (
                        <div className="fb-item-files">
                          {x.files.map((f) => (
                            <a key={f.id} href={fileUrl(f.id)} target="_blank" rel="noopener noreferrer" title={f.name} data-fb-itemfile={f.id}>
                              {f.image ? <img src={fileUrl(f.id)} alt={f.name} /> : <><FileText size={14} aria-hidden="true" /> {f.name}</>}
                            </a>
                          ))}
                        </div>
                      )}
                      {x.reply && <p className="fb-reply"><b>{t('xLoop.fbReply')}:</b> {x.reply}</p>}
                      {x.taskRef && <p className="fb-reply"><b>{t('xLoop.fbTask')}:</b> {x.taskRef}</p>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {preview && (
            <div className="fb-preview" role="dialog" aria-modal="true" aria-label={preview.file.name} onMouseDown={(e) => { if (e.target === e.currentTarget) setPreview(null); }} data-fb-preview>
              <div className="fb-preview-bar"><span>{preview.file.name}</span>
                <button type="button" className="fb-x" onClick={() => setPreview(null)} aria-label={t('xLoop.close')} data-fb-preview-close><X size={18} /></button></div>
              <img src={preview.url} alt={preview.file.name} />
            </div>
          )}
        </div>, document.body)}
    </>
  );
}
